import { spinner } from "@clack/prompts";
import { Cli, Openapi, z } from "incur";
import { createWhopFetch, filterSpecByTag, nativeSpec } from "../api/index.js";

const POLL_INTERVAL_MS = 3_000;

const whopFetch = createWhopFetch();

interface MediaAsset {
	id: string;
	media_type?: string;
	status: "processing" | "ready" | "failed";
	error_message?: string | null;
	amount_charged?: number | null;
	file?: { id: string; url: string } | null;
}

type CtaCommand = {
	command: string;
	description?: string;
	options?: Record<string, string>;
};

type RunCtx = {
	agent: boolean;
	options: Record<string, unknown>;
	error: (opts: {
		code: string;
		message: string;
		retryable?: boolean;
		cta?: { description?: string; commands: CtaCommand[] };
	}) => never;
};

type GeneratedRun = {
	description?: string | undefined;
	args?: z.ZodObject<Record<string, z.ZodType>> | undefined;
	options?: z.ZodObject<Record<string, z.ZodType>> | undefined;
	run: (c: never) => unknown;
};

/**
 * The media group is spec-driven except `generate`, which is hand-written to
 * add `--wait`: generation is async (the API returns a `processing` asset),
 * so waiting collapses the create-then-poll loop into one blocking call that
 * returns the terminal asset with its `file.id` — the value every downstream
 * step (ad creatives, attachments) actually needs.
 */
export async function buildMediaGroup(description: string) {
	const media = Cli.create("media", { description });
	const spec = filterSpecByTag(nativeSpec as Record<string, unknown>, "Media");
	const generated = await Openapi.generateCommands(
		spec as Openapi.OpenAPISpec,
		whopFetch,
		{ config: { security: false } },
	);

	const getEntry = generated.get("get");
	for (const [name, entry] of generated) {
		if (!("run" in entry)) continue;
		if (name === "generate" && getEntry && "run" in getEntry) {
			media.command(name, buildGenerateCommand(entry, getEntry));
			continue;
		}
		media.command(name, entry);
	}

	return media;
}

class GeneratedCommandError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
	}
}

/**
 * Runs a spec-generated command outside incur's dispatch (the create and poll
 * halves of `generate --wait`), converting its `error()` result into a throw
 * so callers can re-map codes and attach CTAs. Requests go through
 * `createWhopFetch`, which owns auth, account fill, and error-message lifting.
 */
async function runGenerated(
	entry: GeneratedRun,
	input: { args?: Record<string, unknown>; options?: Record<string, unknown> },
): Promise<MediaAsset> {
	const context = {
		args: input.args ?? {},
		options: input.options ?? {},
		error: (options: { code: string; message: string }): never => {
			throw new GeneratedCommandError(options.code, options.message);
		},
	};
	return (await entry.run(context as never)) as MediaAsset;
}

function buildGenerateCommand(entry: GeneratedRun, getEntry: GeneratedRun) {
	const options = (entry.options ?? z.object({})).extend({
		wait: z
			.boolean()
			.optional()
			.describe(
				"Block until generation finishes and return the terminal asset (with file.id when ready)",
			),
		timeout: z.coerce
			.number()
			.default(600)
			.describe("Max seconds to wait with --wait before giving up"),
	});

	return {
		description: entry.description,
		options,
		examples: [
			{
				options: {
					type: "image",
					prompt: "A running club jogging across a bridge at sunrise",
					wait: true,
				},
				description: "Generate an image and wait for the finished file",
			},
			{
				options: {
					type: "video",
					prompt: "Sneaker product spin on a white background",
					duration_seconds: 5,
					wait: true,
				},
				description: "Generate a 5s video and wait",
			},
		],
		run: async (c: RunCtx) => {
			const { wait, timeout, ...body } = c.options;

			const asset = await createGeneration(c, entry, body);
			if (!wait) return asset;

			return awaitGeneration(c, getEntry, asset, Number(timeout));
		},
	};
}

async function createGeneration(
	c: RunCtx,
	entry: GeneratedRun,
	body: Record<string, unknown>,
): Promise<MediaAsset> {
	try {
		return await runGenerated(entry, { options: body });
	} catch (err) {
		if (!(err instanceof GeneratedCommandError)) throw err;

		if (err.code === "HTTP_402") {
			return c.error({
				code: "INSUFFICIENT_BALANCE",
				message: err.message,
				retryable: true,
				cta: {
					description: "Fund your balance, then retry:",
					commands: [
						{
							command: "deposits create",
							description: "Add funds to your balance",
						},
					],
				},
			});
		}

		return c.error({ code: err.code, message: err.message, retryable: true });
	}
}

async function awaitGeneration(
	c: RunCtx,
	getEntry: GeneratedRun,
	created: MediaAsset,
	timeoutSeconds: number,
): Promise<MediaAsset> {
	const interactive = !c.agent && process.stdout.isTTY;
	const progress = interactive ? spinner() : null;
	progress?.start(`Generating ${created.media_type ?? "asset"}…`);

	const startedAt = Date.now();
	let asset = created;

	while (asset.status === "processing") {
		if (Date.now() - startedAt > timeoutSeconds * 1000) {
			progress?.stop(`Still processing after ${timeoutSeconds}s.`);
			return c.error({
				code: "WAIT_TIMEOUT",
				message: `Generation ${asset.id} is still processing after ${timeoutSeconds}s. It keeps running server-side — check on it later.`,
				retryable: true,
				cta: {
					description: "Check the asset:",
					commands: [
						{
							command: `media get ${asset.id}`,
							description: "Poll the asset status",
						},
					],
				},
			});
		}

		await sleep(POLL_INTERVAL_MS);
		progress?.message(
			`Generating ${created.media_type ?? "asset"}… ${Math.round((Date.now() - startedAt) / 1000)}s`,
		);
		asset = await fetchAsset(c, getEntry, asset.id, progress);
	}

	if (asset.status === "failed") {
		progress?.stop("Generation failed.");
		return c.error({
			code: "GENERATION_FAILED",
			message: `${asset.error_message ?? "Generation failed."} The charge was refunded — adjust the prompt and retry.`,
			retryable: true,
		});
	}

	progress?.stop(
		`Generated ${asset.file?.id ?? asset.id} in ${Math.round((Date.now() - startedAt) / 1000)}s.`,
	);
	return asset;
}

async function fetchAsset(
	c: RunCtx,
	getEntry: GeneratedRun,
	id: string,
	progress: ReturnType<typeof spinner> | null,
): Promise<MediaAsset> {
	try {
		return await runGenerated(getEntry, { args: { id } });
	} catch (err) {
		if (!(err instanceof GeneratedCommandError)) throw err;
		progress?.stop("Polling failed.");
		return c.error({ code: err.code, message: err.message, retryable: true });
	}
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
